import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, CheckCircle2, MailCheck } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eligibility = [
  ["join.elig1", "Doctorate, PhD, MBBS, or MD"],
  ["join.elig2", "Or double-major post graduation in any stream"],
  ["join.elig3", "Currently working as a professional, anywhere"],
  ["join.elig4", "At least 5 years of experience, in India or abroad"],
];

const qualifications = [
  "PhD / Doctorate",
  "MBBS",
  "MD",
  "Double Major, Post Graduation",
  "Post Graduation (other)",
];

/* the 9 research disciplines (values stay English for stable routing) */
const disciplines = [
  ["disc.d1n", "Space-Time & Matter"],
  ["disc.d2n", "Particle Physics"],
  ["disc.d3n", "Quantum Mechanics"],
  ["disc.d4n", "AI & Computing"],
  ["disc.d5n", "Biology"],
  ["disc.d6n", "Neurology"],
  ["disc.d7n", "Psychology & Cognitive Science"],
  ["disc.d8n", "Health Sciences"],
  ["disc.d9n", "Medical Science"],
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-surface px-5 py-4 text-base text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/50";
const labelCls = "mb-2 block text-left text-base font-medium text-white/70";

export default function JoinUs() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    qualification: "",
    specialization: "",
    discipline: "",
    experience_years: "",
    current_role: "",
    country: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/leads/collaborators`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, experience_years: Number(form.experience_years) }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(96,165,250,0.14),transparent)]" />
      <section data-testid="join-us-page" className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-28 pt-40 md:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <span className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-6 py-3 font-mono text-base uppercase tracking-[0.2em] text-white/80">
            {t("join.badge", "Collaborate With Us")}
          </span>
          <h1 className="mt-10 font-display text-[2rem] font-bold leading-[1.08] tracking-tight sm:text-[2.75rem] lg:text-[3.5rem]">
            {t("join.h1a", "Join the lab as a")} <span className="text-gradient">{t("join.h1b", "collaborator.")}</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg font-light leading-relaxed text-fog">
            {t("join.sub", "We work with exceptional researchers, clinicians, and professionals as interns and collaborators, on the equation of immortality and everything it unlocks.")}
          </p>
        </motion.div>

        <div className="mt-16 grid items-start gap-6 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-6 lg:col-span-2"
          >
            <div data-testid="join-eligibility-card" className="rounded-3xl border border-white/10 bg-surface p-8 text-left md:p-10">
              <h2 className="font-display text-2xl font-semibold tracking-tight">{t("join.elig.h", "Minimum eligibility")}</h2>
              <ul className="mt-6 grid gap-4">
                {eligibility.map(([k, en]) => (
                  <li key={k} className="flex items-start gap-3 text-base font-light text-fog">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
                    {t(k, en)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-white/10 bg-surface/60 p-8 text-left md:p-10">
              <MailCheck className="h-6 w-6 text-cyan-300" />
              <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{t("join.next.h", "What happens next")}</h3>
              <p className="mt-3 text-base font-light leading-relaxed text-fog">
                {t("join.next.p", "Every application is reviewed personally by our research team. You receive an instant email copy of your submission, and we reach out if there is a fit.")}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px] lg:col-span-3"
          >
            <div className="rounded-3xl bg-surface p-8 md:p-10">
              {status === "done" ? (
                <div data-testid="join-success" className="py-10 text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-cyan-300" />
                  <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight">
                    {t("join.success.h", "Application received.")}
                  </h2>
                  <p className="mx-auto mt-4 max-w-md text-lg font-light leading-relaxed text-fog">
                    {t("join.success.p", "A copy of your submission is on its way to your inbox. Our research team reviews every application personally.")}
                  </p>
                </div>
              ) : (
                <form data-testid="join-form" onSubmit={submit} className="grid gap-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="join-name">{t("form.name", "Full name")}</label>
                      <input id="join-name" data-testid="join-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder={t("form.ph.name", "Your name")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="join-email">{t("form.email", "Email")}</label>
                      <input id="join-email" data-testid="join-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@university.edu" />
                    </div>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="join-phone">{t("form.phone", "Contact number")}</label>
                      <input id="join-phone" data-testid="join-phone-input" required className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="join-country">{t("form.country", "Country")}</label>
                      <input id="join-country" data-testid="join-country-input" required className={inputCls} value={form.country} onChange={set("country")} placeholder={t("join.ph.country", "India / abroad")} />
                    </div>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="join-qualification">{t("join.qual", "Highest qualification")}</label>
                      <select id="join-qualification" data-testid="join-qualification-select" required className={inputCls} value={form.qualification} onChange={set("qualification")}>
                        <option value="" disabled>{t("join.qual.select", "Select qualification")}</option>
                        {qualifications.map((q) => (
                          <option key={q} value={q}>{q}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="join-specialization">{t("join.spec", "Specialization / stream")}</label>
                      <input id="join-specialization" data-testid="join-specialization-input" required className={inputCls} value={form.specialization} onChange={set("specialization")} placeholder={t("join.ph.spec", "e.g. Quantum biology, neurology")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="join-discipline">{t("join.disc", "Which discipline fits you best?")}</label>
                    <select id="join-discipline" data-testid="join-discipline-select" required className={inputCls} value={form.discipline} onChange={set("discipline")}>
                      <option value="" disabled>{t("join.disc.select", "Select a discipline")}</option>
                      {disciplines.map(([k, en]) => (
                        <option key={en} value={en}>{t(k, en)}</option>
                      ))}
                      <option value="Not sure yet">{t("join.disc.unsure", "Not sure yet")}</option>
                    </select>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="join-experience">{t("join.exp", "Years of experience")}</label><input id="join-experience" data-testid="join-experience-input" required type="number" min={5} className={inputCls} value={form.experience_years} onChange={set("experience_years")} placeholder={t("join.ph.exp", "Min. 5")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="join-role">{t("join.role", "Current role & organization")}</label>
                      <input id="join-role" data-testid="join-role-input" required className={inputCls} value={form.current_role} onChange={set("current_role")} placeholder={t("join.ph.role", "e.g. Research Scientist, CSIR")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="join-message">{t("join.why", "Why do you want to collaborate?")}</label>
                    <textarea id="join-message" data-testid="join-message-input" required rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder={t("join.ph.why", "Your research interests, publications, what you would work on...")} />
                  </div>
                  {status === "error" && (
                    <p data-testid="join-error" className="text-base text-rose-300">
                      {t("form.error", "Something went wrong. Please try again or email invest@thelifespanproject.com.")}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="join-submit-button"
                    disabled={status === "sending"}
                    className="group flex items-center justify-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] disabled:opacity-60 sm:px-8 sm:py-4"
                  >
                    {status === "sending" ? t("join.submitting", "Submitting...") : t("join.submit", "Apply as Collaborator")}
                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
